/**
 * UserPointHistoryContainer
 * --
 */
import React, { useState, useEffect, useMemo } from 'react';
import MessageAlert from 'utils';
import UserPointHistoryPresenter from './UserPointHistoryPresenter';
import {
  useGetPointHistories,
  useUpsertPointHistory,
  useUsePointHistory,
} from 'hooks/PointHistoryHooks';
import { useGetSetting } from 'hooks/SettingHooks';
import dayjs from 'dayjs';
import { useGetShopInformations } from 'hooks/ShopInformationHooks';
import { useGetUsers } from 'hooks/UserHooks';
import { useGetPointHistoryLogs } from 'hooks/PointHistoryLogHooks';

/**----------------------------------------
 * [Component] 총관리자: 회원관리 -> 포인트내역
 * ----------------------------------------
 */
const UserPointHistoryContainer = ({ permission }) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  const [loading, setLoading] = useState(false);

  const [currentUser, setCurrentUser] = useState(null);
  const [period, setPeriod] = useState({
    start_date: dayjs().subtract(1, 'months').startOf('day').unix(),
    end_date: dayjs().endOf('day').unix(),
  });
  const [inputValue, setInputValue] = React.useState({
    point: 0,
    expired_date: 0,
    memo: null,
  });
  const [selectedList, setSelectedList] = useState([]);

  const confirm = (msg = '') => window.confirm(msg);

  /* ===== MUTATE ===== */
  const { data: point_setting } = useGetSetting(1, null, {
    select: (res) => {
      const { status, data } = res;

      if (status !== 200) return null;

      return data.point_setting;
    },
    initialData: null,
  });
  const { data: ShopInformationList } = useGetShopInformations(null, {
    select: (res) => {
      const { status, data } = res;

      if (status !== 200) {
        return [];
      }

      return data;
    },
    initialData: [],
  });
  const { data: UserList } = useGetUsers(
    { filter: 'user_name' },
    {
      select: (res) => {
        const { status, data } = res;

        if (status !== 200) {
          return [];
        }

        return data;
      },
      initialData: [],
    }
  );
  const { data: PointHistoryList, refetch: refetchPointHistoryList } =
    useGetPointHistories(
      { start_date: period.start_date, end_date: period.end_date },
      {
        select: (res) => {
          const { status, data } = res;

          if (status !== 200) {
            return [];
          }

          return data;
        },
        initialData: [],
      }
    );
  const { data: PointHistoryLogList, refetch: refetchPointHistoryLogList } =
    useGetPointHistoryLogs(
      { start_date: period.start_date, end_date: period.end_date },
      {
        select: (res) => {
          const { status, data } = res;

          if (status !== 200) {
            return [];
          }

          return data;
        },
        initialData: [],
      }
    );
  const { usedPointHistory } = useUsePointHistory();
  const { upsertPointHistory } = useUpsertPointHistory();
  const newUserList = useMemo(() => {
    const userList = UserList?.map((user) => {
      const userPointHistory = PointHistoryList?.filter(
        (point) => point.user_id === user.user_id
      ).map((point) => {
        const matchedShop = ShopInformationList?.find(
          (shop) => point?.shop_information_id === shop?.shop_information_id
        );
        const matchedPointLog = PointHistoryLogList?.filter(
          (logs) => logs.point_history_id === point.point_history_id
        ).map((log) => ({
          key: log?.point_history_log_id,
          ...log,
          shop_information_name:
            matchedShop?.shop_information_name || '삭제된 매장',
        }));
        return {
          key: point?.point_history_id,
          ...point,
          shop_information_name:
            matchedShop?.shop_information_name || '삭제된 매장',
          point_history_log: matchedPointLog,
        };
      });

      return {
        key: user?.user_id,
        ...user,
        point_history: userPointHistory,
      };
    });
    return userList;
  }, [PointHistoryList, UserList, ShopInformationList, PointHistoryLogList]);
  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */

  /**
   *
   * @param {string} t
   * @param {string | number} v
   */
  const handleChange = (t, v) => {
    setInputValue((prev) => ({
      ...prev,
      [t]: v,
    }));
  };
  const refreshData = () => {
    refetchPointHistoryList();
    refetchPointHistoryLogList();
  };

  /**--------------------
   * 적립금 지급
   * --------------------
   * @param {object | array} data
   * @param {number} reserve
   */
  const handleReservePoint = async (data, v, t, callback) => {
    const dataType = Array.isArray(data)
      ? 'array'
      : typeof data === 'object'
      ? 'object'
      : null;

    if (!dataType) return;

    try {
      const msg =
        dataType === 'array'
          ? `${data?.length}명에게 ${v?.point} 적립금을 지급하시겠습니까?`
          : `${data.user_name}님에게 ${v?.point} 적립금을 지급하시겠습니까?`;

      if (!confirm(msg)) return;

      setLoading(true);

      let res, update;
      if (t === 'REWARD') {
        switch (dataType) {
          case 'array':
            // selectedList;
            update = data?.map((user) => ({
              user_id: user?.user_id,
              point: v.point,
              shop_information_id: 1,
              type: 'EARNED',
              expired_date: dayjs()
                .add(point_setting?.expired_date, 'day')
                .unix(),
            }));

            res = await upsertPointHistory(update);

            if (res.status !== 200) {
              MessageAlert.error('적립금 지급에 실패하였습니다!');
              return;
            }

            MessageAlert.success('적립금이 지급되었습니다.');
            setSelectedList([]);
            callback();
            break;
          case 'object':
            if (!v.point) {
              MessageAlert.info('적립하실 적립금을 입력해주세요.');
              return;
            }
            if (!v.expired_date) {
              if (
                !confirm(
                  `만료일을 설정하지 않으면 관리자설정에서 설정한 만료일을\n기준으로 들어갑니다. 진행하시겠습니까?\n관리자 설정 : 적립일 기준 ${point_setting?.expired_date} 일`
                )
              )
                return;
            }
            update = {
              user_id: data?.user_id,
              point: v.point,
              shop_information_id: 1,
              type: 'EARNED',
              expired_date: dayjs()
                .add(
                  v.expired_date ? v.expired_date : point_setting?.expired_date,
                  'day'
                )
                .unix(),
              memo: v.memo,
            };
            res = await upsertPointHistory([update]);
            if (res.status !== 200) {
              MessageAlert.error('적립금 지급에 실패하였습니다!');
              return;
            }
            refreshData();
            MessageAlert.success('적립금이 지급되었습니다.');
            break;
          default:
            return;
        }
      }

      if (t === 'DEDUCTED') {
        if (!v.point) {
          MessageAlert.info('차감하실 적립금을 입력해주세요.');
          return;
        }

        if (!confirm(`${v.point}P만큼 차감하시겠습니까?`)) return;

        res = await usedPointHistory({
          user_id: data.user_id,
          use_point: v.point,
          memo: v.memo,
        });

        if (res.status !== 200) {
          MessageAlert.info('적립금 차감에 실패하였습니다.');
          return;
        }

        MessageAlert.success('적립금이 차감되었습니다.');
        refreshData();
      }
    } catch (err) {
      console.error('[handleReservePoint] Error!! ', err);
      MessageAlert.error('적립금 지급/차감에 실패하였습니다!');
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  /**
   * 회원 선택 핸들러
   * --
   * @param {string} type
   * @param {object | array} obj
   */
  const handleSelect = (type, obj) => {
    const selectType = type.toLocaleUpperCase();

    switch (selectType) {
      case 'CHECKED': // 개별 선택
        setSelectedList((prev) => [...prev, obj]);
        return;
      case 'UNCHECKED': // 개별 선택 해제
        setSelectedList((prev) =>
          prev.filter((select) => select.key !== obj.key)
        );
        return;
      case 'ALL-CHECKED': // 노출된 목록 전체 선택
        setSelectedList(obj);
        return;
      case 'ALL-UNCHECKED': // 노출된 목록 전체 선택 해제
        const removeList = selectedList.filter((user) =>
          obj.some((ob) => ob.key === user.key)
        );

        setSelectedList(removeList);

        return;
      case 'ALL': // 검색된 회원 전체 선택/해제
        const isAll = selectedList.length === obj.length;

        if (!isAll) {
          setSelectedList(obj);
        } else {
          setSelectedList([]);
        }
        return;
      default:
        return;
    }
  };

  /* ===== HOOKS ===== */
  // /* 초기 설정 & 업체변경 시 반영 */
  // useEffect(() => {
  //   currentShop && handleGetReviewList();
  // }, [currentShop]);

  /* ===== RENDER ===== */
  return (
    <UserPointHistoryPresenter
      {...{
        /* States */
        userList: newUserList,
        permission,
        loading,
        currentUser,
        setCurrentUser,
        period,
        setPeriod,
        PointHistoryList,
        PointHistoryLogList,
        onChange: handleChange,
        inputValue: inputValue,
        setInputValue: setInputValue,
        /* Events */
        setLoading,
        onReservePoint: handleReservePoint,
        onSelect: handleSelect,
        selectedList,
      }}
    />
  );
};

/* DF */
UserPointHistoryContainer.defaultProps = {
  defaultShopId: null,
  permission: 'ADMIN',
};
export default UserPointHistoryContainer;
